
import { v4 as uuidv4 } from "uuid";
import { 
  FeatureSource,
  IStringNumHash,
  PlayerObject,
  SmartHash, 
} from "@models";
import { 
  Creature,
  AbilityScores,
  HitDice, 
  CharacterFeature,
  UpgradableNumber,
  CharacterAbility,
  CreatureHP,
  CharacterResourceHolder,
  CreatureGift,
  DamageTypeOverridesTable,
  CharacterSubscriptionHolder,
  CharacterItem,
  CharacterRace,
  SKILLS,
  CharacterCondition,
  CreatureAbility,
  Character,
  CharacterSheetInterface,
  AlternateConditionFeature,
  Bonus,
  CharacterAbilityModifier,
  CharacterAction,
  CharacterAdvantage,
  CharacterASIBaseFeature,
  CharacterExtraEffects,
  CharacterFeatureBase,
  CharacterInfusion,
  CharacterInfusionsOfLevel,
  CharacterProficiency,
  CharacterResource,
  CharacterRoll,
  CharacterSense,
  CharacterSlot,
  CharacterSpell,
  Customization,
  DamageMultiplierSimple,
  GreaterAdvantage,
  MinimumRoll,
  Modifier,
  RangeBonus,
  Reroll,
  SizeOptions,
  SkillAbilityOverrideFeature,
  SummonInstance,
  Transform,
  TransformInstance,
  CharacterFeatureSource,
  CharacterFeat,
  Ability,
  Summon,
  CreatureDetails,
  MinionAbility,
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  HIT_DICE_SIZE_MAP, 
  SIZE_MAP, 
  SIZE_TO_NUM_MAP 
} from "@dndModels/Constants";
import { AbilityScoreType, CreatureType, DamageType, DataType, isDamageType, isSizeType, SizeType, SpeedType, SpellLevel } from "@dndModels/Types";
import DataUtilities from "@dndUtilities/data_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

const data_util = DataUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export class BasicCreatureInstance implements CreatureDetails {
  true_id = "";
  _name = "";
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  changed() {
    this.subscription_holder.characterChanged();
  }
  get name() {
    let name = this._name;
    if (name === "" && this.creature) {
      name = this.creature.name_for_cr(this.challenge_rating);
      for (const cg of this.creature_gifts) {
        const gift = cg.gift;
        if (gift) {
          if (gift.name_formula.length > 0) {
            let new_name = gift.name_formula;
            if (gift.name_formula.includes("replace.")) {
              // debugger;
              // {replace.Dragon.Shadow Dragon}
              new_name = new_name.replace("{","").replace("}","");
              const pieces = new_name.split(".");
              if (pieces.length === 3) {
                new_name = name.replace(pieces[1], pieces[2]);
              }
            } else {
              new_name = new_name.replace("{base.name}", name);
              new_name = new_name.replace("{gift.name}", gift.name);
              new_name = new_name.replace("{gift.level}", cg.level > 1 ? `${cg.level}` : "");
            }
            name = new_name;
          }
        }
      }
      if (this._num > 1) {
        name += " " + this._num;
      }
    }
    return name;
  }
  set name(value: string) {
    this._name = value;
  }
  _num = 1;
  get description() {
    let description = this.creature ? this.creature.description : "";
    for (const cg of this.creature_gifts) {
      const gift = cg.gift;
      if (gift && gift.description.length > 0) {
        description += `\r\n\r\n <b>${gift.name}</b>\r\n ${gift.description}`;
      }
    }
    return description
  }
  stat_block_id = "";
  connected = false;
  is_new = true;
  base_slot_level = -1;
  slot_level = -1;
  
  challenge_rating = 0;
  challenge_rating_modifier = 0;
  get use_challenge_rating() {
    return this.challenge_rating + this.challenge_rating_modifier;
  }

  get swarm() {
    return this.creature ? this.creature.swarm : false;
  }
  
  override_creature_type?: CreatureType;
  get creature_type() {
    if (this.override_creature_type) {
      return this.override_creature_type;
    }
    // debugger;
    let creature_type = this.creature ? this.creature.creature_type : "None";
    const overrider = this.creature_gifts.find(o => o.gift && o.gift.override_type !== "None");
    if (overrider && overrider.gift) {
      creature_type = overrider.gift.override_type;
    }
    return creature_type;
  }
  set creature_type(value: CreatureType) {
    this.override_creature_type = value;
  }
  get subtypes() {
    let subtypes = this.creature ? this.creature.subtypes : [];
    for (const cg of this.creature_gifts) {
      if (cg.gift) {
        let gift_subtypes = cg.gift.bonus_subtypes;
         
        subtypes = [...subtypes,...gift_subtypes.filter(o => !subtypes.includes(o))];
        gift_subtypes = cg.gift.remove_subtypes;
        subtypes = subtypes.filter(o => !gift_subtypes.includes(o));
      }
    }
    return subtypes;
  }
  get tags() {
    return this.creature?.tags??[];
  }
  get age_category() {
    return this.creature?.age_category_for_cr(this.challenge_rating)??"";
  }
  get creature_type_string() {
    if (this.swarm) {
      return `Swarm of ${this.size} ${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    } else {
      return `${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    }
  }

  resource_holder: CharacterResourceHolder = new CharacterResourceHolder();
  get hp() {
    return this.resource_holder.hp;
  }
  get hit_dice() {
    return this.resource_holder.hit_dice;
  }
  hit_dice_bonus = 0;
  extra_hp_pools: CreatureHP[] = [];

  share_initiative = false;
  initiative_modifier = 0;
  armor_class = 0;

  get use_ac() {
    return this.armor_class;
  }
  size = "";

  get use_size() {
    return this.size_num;
  }
  get size_num() {
    return SIZE_TO_NUM_MAP[this.size];
  }
  alignment = "";
  // attributes
  current_speed: IStringNumHash = {
    walk: 0,
    swim: 0,
    climb: 0,
    fly: 0,
    burrow: 0,
    hover: 0
  };
  speed: IStringNumHash = {
    walk: 0,
    swim: 0,
    climb: 0,
    fly: 0,
    burrow: 0,
    hover: 0
  };
  get flying() {
    return this.current_speed.fly > 0;
  }
  get swimming() {
    return this.current_speed.swim > 0;
  }
  current_senses: IStringNumHash = {};
  senses: IStringNumHash = {};
  passives: IStringNumHash = {
    passive_perception: 0,
    passive_insight: 0,
    passive_investigation: 0
  };

  favorite_conditions: string[] = [];
  // condition_hash: { [id: string]: { level: number, source_true_id: string } } = {};
  condition_immunities: string[] = [];
  conditions: CharacterCondition[] = [];

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }

  // get damage_immunities() {
    
  // }
  damage_immunities: DamageType[] = [];
  extra_damage_immunities = "";
  damage_resistances: DamageType[] = [];
  extra_damage_resistances = "";
  damage_vulnerabilities: DamageType[] = [];
  extra_damage_vulnerabilities = "";

  tool_proficiencies: string[] = [];
  skill_proficiencies2: { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] = [];
  saving_throws: IStringNumHash = {
    STR: 0, DEX: 0, CON: 0, 
    INT: 0, WIS: 0, CHA: 0
  };
  languages = "";
  
  current_ability_scores: AbilityScores = new AbilityScores();
  ability_scores: AbilityScores = new AbilityScores();

  use_resources: {
    resource_id: string, // Can be "Slot:Level" or "Pact:Level"
    used: number,
    total: number,
    size: number
  }[] = [];

  actions: CharacterFeature[] = [];
  traits: CharacterFeature[] = [];
  legendary_actions: CharacterFeature[] = [];
  lair_actions: CharacterFeature[] = [];
  abilities: CharacterAbility[] = [];
  
  mishaps: CharacterFeature[] = [];
  stations: CharacterFeature[] = [];

  get action_cfs() {
    return this.actions.filter(o => 
      !(o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) || 
      (o.floating_feature.casting_time !== "BA" && o.floating_feature.casting_time !== "RA"));
  }
  get bonus_actions() {
    return this.actions.filter(o => 
      (o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) && 
      o.floating_feature.casting_time === "BA");
  }
  get reactions() {
    return this.actions.filter(o => 
      (o.floating_feature instanceof MinionAbility || 
        o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability) && 
      o.floating_feature.casting_time === "RA");
  }

  used_legendary_actions = 0;
  legendary_actions_per_round = 0;
  used_legendary_resistances = 0;
  legendary_resistances = 0;
  used_slots: { [level: number]: number } = {};
  extra_attacks = 0;
  extra_attacks_string = "";

  creature_id = "";
  creature?: Creature;
  creature_gifts: CreatureInstanceGift[] = [];

  humanoid_race_id = "";
  humanoid_subrace_id = "";
  humanoid_race: CharacterRace[] = [];

  gear_items: CharacterItem[] = [];

  get proficiency_modifier(): number {
    return common_util.proficiencyBonus(this.use_challenge_rating);
  }

  get cgift_ids(): string[] {
    return [];
  }

  value = 0;
  notes = "";

  constructor(obj?: any) {
    this.is_new = obj ? false : true;
    this.true_id = obj ? obj.true_id : uuidv4().toString();
    if (obj) {
      // console.log(obj);
      // console.log(this);
      this.stat_block_id = obj.stat_block_id ? obj.stat_block_id : "";
      this.creature_id = obj.creature_id ? obj.creature_id : "";
      this.creature_gifts = obj.creature_gifts ? obj.creature_gifts.map((o: any) => new CreatureInstanceGift(o)) : [];
      this.base_slot_level = obj.base_slot_level ? obj.base_slot_level : -1;
      this.slot_level = obj.slot_level ? obj.slot_level : -1;
      this._name = obj.name ? obj.name : "";
      this._num = obj._num ? obj._num : 1;
      this.challenge_rating = obj.challenge_rating ? obj.challenge_rating : 0;
      this._image_url = obj.image_url ? obj.image_url : "";
      this.current_ability_scores = obj.current_ability_scores ? new AbilityScores(obj.current_ability_scores) : new AbilityScores();
      this.ability_scores = obj.ability_scores ? new AbilityScores(obj.ability_scores) : new AbilityScores();
    
      this.use_resources = obj.resources ? obj.resources : [];
      this.use_resources.forEach(r => {
        if (!r.size) {
          r.size = 1;
        }
      });

      this.actions = [];
      if (obj.actions && obj.actions.length > 0) {
        obj.actions.forEach((o: any) => {
          this.actions.push(new CharacterFeature(o));
        });
      }

      this.traits = [];
      if (obj.special_abilities && obj.special_abilities.length > 0) {
        obj.special_abilities.forEach((o: any) => {
          this.traits.push(new CharacterFeature(o));
        });
      }

      this.legendary_actions = [];
      this.legendary_actions_per_round = obj.legendary_actions_per_round ? obj.legendary_actions_per_round : 0;
      this.used_legendary_actions = obj.used_legendary_actions ? obj.used_legendary_actions : 0;
      this.used_slots = obj.used_slots ? {...obj.used_slots} : {};
      if (obj.legendary_actions && obj.legendary_actions.length > 0) {
        obj.legendary_actions.forEach((o: any) => {
          this.legendary_actions.push(new CharacterFeature(o));
        });
      }
      this.legendary_resistances = obj.legendary_resistances ? obj.legendary_resistances : 0;
      this.used_legendary_resistances = obj.used_legendary_resistances ? obj.used_legendary_resistances : 0;

      this.lair_actions = [];
      if (obj.lair_actions && obj.lair_actions.length > 0) {
        obj.lair_actions.forEach((o: any) => {
          this.lair_actions.push(new CharacterFeature(o));
        });
      }

      this.abilities = [];
      if (obj.abilities && obj.abilities.length > 0) {
        obj.abilities.forEach((o: any) => {
          this.abilities.push(new CharacterAbility(o));
        });
      }
      
      if (obj.resource_holder) {
        this.resource_holder = new CharacterResourceHolder(obj.resource_holder);
      } else {
        this.resource_holder = new CharacterResourceHolder();
        this.resource_holder.hp = obj.hp ? new CreatureHP(obj.hp) : new CreatureHP();
        this.resource_holder.hit_dice = []; 
        if (obj.hit_dice) {
          if (obj.hit_dice.length) {
            obj.hit_dice.forEach((hd: any) => {
              this.resource_holder.hit_dice.push(new HitDice(hd));
            });
          } else {
            this.resource_holder.hit_dice.push(new HitDice(obj.hit_dice));
          }
        }
      }
      this.hit_dice_bonus = obj.hit_dice_bonus ? obj.hit_dice_bonus : 0;
      
      this.current_speed = obj.current_speed ? obj.current_speed : {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0,
        hover: 0
      };
      this.speed = obj.speed ? obj.speed : {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0,
        hover: 0
      };
      this.passives = obj.passives ? obj.passives : {
        passive_perception: 0,
        passive_insight: 0,
        passive_investigation: 0
      };
      this.saving_throws = obj.saving_throws ? obj.saving_throws : {
        STR: 0, DEX: 0, CON: 0, 
        INT: 0, WIS: 0, CHA: 0
      };
      this.languages = obj.languages ? obj.languages : "";
      this.tool_proficiencies = [];
      if (obj.tool_proficiencies && obj.tool_proficiencies.length > 0) {
        this.tool_proficiencies = obj.tool_proficiencies;
      }
      this.skill_proficiencies2 = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : [];
      this.initiative_modifier = obj.initiative_modifier ? obj.initiative_modifier : 0;
      this.armor_class = obj.armor_class ? obj.armor_class : 0;
      this.size = obj.size ? obj.size : "Medium";
      this.alignment = obj.alignment ? obj.alignment : "Neutral";
      // this.xp = obj.xp ? obj.xp : 0;
     
      this.favorite_conditions = obj.favorite_conditions ? obj.favorite_conditions : [];
      this.conditions = [];
      if (obj && obj.conditions) {
        for (let i = 0; i < obj.conditions.length; i++) {
          const cc = new CharacterCondition(obj.conditions[i]);
          this.conditions.push(cc);
        }
      }
      this.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      
      this.extra_damage_immunities = obj.extra_damage_immunities ? obj.extra_damage_immunities : "";
      let damage_immunities: string[] = [];
      if (obj.damage_immunities && obj.damage_immunities.length > 0) {
        damage_immunities = obj.damage_immunities;
      } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        obj.damage_multipliers.filter((o: any) => o.multiplier === 0).forEach((dm: any) => {
          damage_immunities = [...damage_immunities,...dm.damage_types];
        });
      }
      damage_immunities = damage_immunities.filter(o => o && o !== "");
      let non_fitting = damage_immunities.filter(o => !isDamageType(o));
      this.damage_immunities = damage_immunities.map(o => {
        if (o === null || o.length === 0) {
          debugger;
        }
        const dt = o[0].toLocaleUpperCase() + o.substring(1);
        if (isDamageType(dt)) {
          return dt;
        }
        return "";
      }).filter(o => o !== "") as DamageType[];
      if (non_fitting.length > 0) {
        if (this.extra_damage_immunities.length > 0) {
          this.extra_damage_immunities += ", ";
        }
        this.extra_damage_immunities += non_fitting.join(", ");
      }
      this.extra_damage_resistances = obj.extra_damage_resistances ? obj.extra_damage_resistances : "";
      let damage_resistances: string[] = [];
      if (obj.damage_resistances && obj.damage_resistances.length > 0) {
        damage_resistances = obj.damage_resistances;
      } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        obj.damage_multipliers.filter((o: any) => o.multiplier === 0).forEach((dm: any) => {
          damage_resistances = [...damage_resistances,...dm.damage_types];
        });
      }
      damage_resistances = damage_resistances.filter(o => o && o !== "");
      non_fitting = damage_resistances.filter(o => !isDamageType(o));
      this.damage_resistances = damage_resistances.map(o => {
        const dt = o[0].toLocaleUpperCase() + o.substring(1);
        if (isDamageType(dt)) {
          return dt;
        }
        return "";
      }).filter(o => o !== "") as DamageType[];
      this.extra_damage_vulnerabilities = obj.extra_damage_vulnerabilities ? obj.extra_damage_vulnerabilities : "";
      let damage_vulnerabilities: string[] = [];
      if (obj.damage_vulnerabilities && obj.damage_vulnerabilities.length > 0) {
        damage_vulnerabilities = obj.damage_vulnerabilities;
      } else if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        obj.damage_multipliers.filter((o: any) => o.multiplier === 0).forEach((dm: any) => {
          damage_vulnerabilities = [...damage_vulnerabilities,...dm.damage_types];
        });
      }
      damage_vulnerabilities = damage_vulnerabilities.filter(o => o && o !== "");
      non_fitting = damage_vulnerabilities.filter(o => !isDamageType(o));
      this.damage_vulnerabilities = damage_vulnerabilities.map(o => {
        const dt = o[0].toLocaleUpperCase() + o.substring(1);
        if (isDamageType(dt)) {
          return dt;
        }
        return "";
      }).filter(o => o !== "") as DamageType[];
      
      this.senses = {};
      if (obj.senses) {
        if (obj.senses.length > 0) {
          obj.senses.forEach((sense: any) => {
            console.log(sense);
            // this.senses[sense.name] = sense.range;
            // this.senses.push(new CharacterSense(sense));
          });
        } else {
          this.senses = obj.senses;
        }
      }
      this.current_senses = {};
      if (obj.current_senses) {
        if (obj.current_senses.length > 0) {
          obj.current_senses.forEach((sense: any) => {
            console.log(sense);
            // this.senses[sense.name] = sense.range;
            // this.current_senses.push(new CharacterSense(sense));
          });
        } else {
          this.current_senses = obj.current_senses;
        }
      }

      this.value = obj.value ? obj.value : 0;
      this.notes = obj.notes ? obj.notes : "";
    }
  }
  get min_challenge_rating() { return this.challenge_rating; }
  get max_challenge_rating() { return this.challenge_rating; }
  get _id() { return this.creature?._id??""; }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      stat_block_id: this.stat_block_id,
      creature_id: this.creature_id,
      creature_gifts: this.creature_gifts.map(o => o.toDBObj()),
      base_slot_level: this.base_slot_level,
      slot_level: this.slot_level,
      instance_type: "Creature",
      name: this._name,
      _num: this._num,
      current_ability_scores: this.current_ability_scores.toDBObj(),
      ability_scores: this.ability_scores.toDBObj(),
      hit_dice_bonus: this.hit_dice_bonus,
      resources: this.use_resources,
      actions: this.actions.map(o => o.toDBObj()),
      legendary_actions: this.legendary_actions.map(o => o.toDBObj()),
      legendary_actions_per_round: this.legendary_actions_per_round,
      used_legendary_actions: this.used_legendary_actions,
      used_slots: {...this.used_slots},
      lair_actions: this.lair_actions.map(o => o.toDBObj()),
      legendary_resistances: this.legendary_resistances,
      used_legendary_resistances: this.used_legendary_resistances,
      special_abilities: this.traits.map(o => o.toDBObj()),
      abilities: this.abilities.map(o => o.toDBObj()),
      challenge_rating: this.challenge_rating,
      resource_holder: this.resource_holder.toDBObj(),
      current_speed: this.current_speed,
      speed: this.speed,
      passives: this.passives,
      saving_throws: this.saving_throws,
      skill_proficiencies2: this.skill_proficiencies2,
      tool_proficiencies: this.tool_proficiencies,
      initiative_modifier: this.initiative_modifier,
      armor_class: this.armor_class,
      size: this.size,
      alignment: this.alignment,
      // xp: this.xp,
      favorite_conditions: this.favorite_conditions,
      // condition_hash: this.condition_hash,
      conditions: this.conditions.map(o => o.toDBObj()),
      condition_immunities: this.condition_immunities,
      damage_immunities: this.damage_immunities,
      extra_damage_immunities: this.extra_damage_immunities,
      damage_resistances: this.damage_resistances,
      extra_damage_resistances: this.extra_damage_resistances,
      damage_vulnerabilities: this.damage_vulnerabilities,
      extra_damage_vulnerabilities: this.damage_vulnerabilities,
      languages: this.languages,
      value: this.value,
      notes: this.notes,
      image_url: this.image_url,
    };
  }

  get_use_name(): string {
    if (this.creature) {
      return this.creature.name_for_cr(this.challenge_rating);
    }
    return "";
  }

  get_speed(type: string): number {
    const the_speed = (this.speed as any)[type];
    if (the_speed) {
      return the_speed;
    }
    return 0;
  }

  get hit_dice_string(): string {
    let response = "";
    let hd_bonus = 0;
    for (const hd of this.hit_dice) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
      hd_bonus += hd.count * this.hit_dice_bonus;
    }
    if (hd_bonus !== 0) {
      response += ` ${common_util.addPlusMaybe(hd_bonus)}`;
    }
    return response;
  }

  get skills_string(): string {
    return this.skill_proficiencies2.map(o => { return `${o.skill_name}: ${o.modifier < 0 ? o.modifier : "+" + o.modifier}`; }).join(", ");
  }

  get tools_string(): string {
    return this.tool_proficiencies.join(", ");
  }

  get senses_string() {
    let response = "";
    Object.keys(this.current_senses).forEach(key => {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${key} ${this.current_senses[key]} ft.`;
    });
    // if (this.passives.passive_perception > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Perception: ${this.passives.passive_perception}`;
    // }
    // if (this.passives.passive_insight > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Insight: ${this.passives.passive_insight}`;
    // }
    // if (this.passives.passive_investigation > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Investigation: ${this.passives.passive_investigation}`;
    // }
    return response;
  }
  get passive_perception() {
    let passive_perception = 10 + this.current_ability_scores.WIS;
    const perception = this.skill_proficiencies2.find(o => o.skill_id === "5fef742e7494ba20cca8a9ec" || o.skill_id === "Perception");
    if (perception) {
      passive_perception += this.proficiency_modifier * perception.prof_level;
    }
    return passive_perception;
  }
  get small_name() {
    return this.creature ? this.creature.small_name : this.name;
  }

  get_saving_throw(ability: AbilityScoreType) {
    let modifier: number | null | undefined = this.saving_throws[ability];
    if (!modifier) {
      modifier = this.current_ability_scores.getModifier(ability);
    }
    if (!modifier) {
      return 0
    } else {
      return modifier;
    }
  }
  saving_throw(ability: AbilityScoreType) {
    return this.get_saving_throw(ability);
  }

  get speed_string(): string {
    let response = "";
    const speeds = Object.keys(this.current_speed);
    for (const key of speeds) {
      if (this.current_speed[key]) {
        if (response.length > 0) {
          response += ", ";
        }
        if (key === "hover") {
          if (this.current_speed[key] === 1) {
            response += "hover";
          } else {
            response += "no hover";
          }
        } else {
          response += `${key} ${this.current_speed[key]} ft.`;
        }
      }
    }
    return response;
  }

  get immunities_string(): string {
    let str = this.damage_immunities.join(", ");
    if (this.extra_damage_immunities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_immunities;
    }
    return str;
  }

  get resistances_string(): string {
    let str = this.damage_resistances.join(", ");
    if (this.extra_damage_resistances.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_resistances;
    }
    return str;
  }

  get vulnerabilities_string(): string {
    let str = this.damage_vulnerabilities.join(", ");
    if (this.extra_damage_vulnerabilities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_vulnerabilities;
    }
    return str;
  }

  get condition_immunities_string(): string {
    return this.condition_immunities.join(", ");
  }

  get true_max_hit_points() {
    return this.hp.true_max;
  }

  get spellcasting_ability() {
    return this.creature?.spellcasting_ability??"None";
  }

  get spell_dc() {
    return this.creature ? this.creature.spell_dc : new UpgradableNumber();
  }

  get dc() {
    return this.spell_dc;
  }

  get spell_attack() {
    return this.creature ? this.creature.spell_attack : new UpgradableNumber();
  }

  get non_magic_attack() {
    return this.creature ? this.creature.non_magic_attack : new UpgradableNumber();
  }

  get magic_dmg_bonus() {
    return this.creature ? this.creature.magic_dmg_bonus : new UpgradableNumber();
  }

  get non_magic_dmg_bonus() {
    return this.creature ? this.creature.non_magic_dmg_bonus : new UpgradableNumber();
  }

  get spellcasting_description(): string {
    if (this.creature) {
      const spellcasting_entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.description;
      }
    }
    return "";
  }

  get use_spells() {
    if (this.creature) {
      const spellcasting_entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.spells;
      }
    }
    return [];
  }

  get spell_levels() {
    if (this.creature) {
      const spellcasting_entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.spell_levels;
      }
    }
    return [];
  }

  x_per_day_for(spell_id: string) {
    if (this.creature) {
      const entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (entry) {
        const spell = entry.spells.find(o => o.spell_id === spell_id);
        if (spell) {
          return spell.x;
        }
      }
    }
    return 0;
  }

  get string_hash() {
    return this.creature ? this.creature.string_hash : {};
  }

  get upgradable_hash() {
    return this.creature ? this.creature.upgradable_hash : {};
  }

  get use_slots2() {
    return this.use_resources.filter(o => o.resource_id.includes("Slot"));
  }

  _image_url = "";
  get image_url() {
    if (this._image_url === "") {
      return this.creature?.image_url??"";
    }
    return this._image_url;
  }
  set image_url(value: string) {
    this._image_url = value;
  }

  copy(copyMe: BasicCreatureInstance, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this._name = copyMe._name;
    this._num = copyMe._num;
    this.creature = copyMe.creature;
    this.creature_id = copyMe.creature_id;
    this.creature_gifts = copyMe.creature_gifts.map(o => o.clone());
    this.current_ability_scores = new AbilityScores(copyMe.current_ability_scores);
    this.ability_scores = new AbilityScores(copyMe.ability_scores);
    this.hit_dice_bonus = copyMe.hit_dice_bonus;
    this.challenge_rating = copyMe.challenge_rating;

    this.resource_holder = copyMe.resource_holder.clone();

    this.current_speed = {...copyMe.current_speed};
    this.speed = {...copyMe.speed};
    this.passives = {...copyMe.passives};
    this.saving_throws = {...copyMe.saving_throws};
    this.skill_proficiencies2 = copyMe.skill_proficiencies2.map(o => { return { ...o }; });
    this.tool_proficiencies = [...copyMe.tool_proficiencies];
    this.initiative_modifier = copyMe.initiative_modifier;
    this.armor_class = copyMe.armor_class;
    this.use_resources = copyMe.use_resources.map(o => { return {...o}; });
    this.actions = copyMe.actions.map(o => o.clone());
    this.legendary_actions = copyMe.legendary_actions.map(o => o.clone());
    this.legendary_actions_per_round = copyMe.legendary_actions_per_round;
    this.used_legendary_actions = copyMe.used_legendary_actions;
    this.legendary_resistances = copyMe.legendary_resistances;
    this.used_legendary_resistances = copyMe.used_legendary_resistances;
    this.used_slots = {...copyMe.used_slots};
    this.lair_actions = copyMe.lair_actions.map(o => o.clone());

    this.traits = copyMe.traits.map(o => o.clone());
    this.abilities = copyMe.abilities.map(o => o.clone());

    this.size = copyMe.size;
    this.alignment = copyMe.alignment;
    // this.xp = copyMe.xp;
    this.favorite_conditions = [...copyMe.favorite_conditions];
    // this.condition_hash = {...copyMe.condition_hash};
    this.conditions = copyMe.conditions.map(o => o.clone());
    this.condition_immunities = [...copyMe.condition_immunities];

    this.damage_immunities = [...copyMe.damage_immunities];
    this.extra_damage_immunities = copyMe.extra_damage_immunities;
    this.damage_resistances = [...copyMe.damage_resistances];
    this.extra_damage_resistances = copyMe.extra_damage_resistances;
    this.damage_vulnerabilities = [...copyMe.damage_vulnerabilities];
    this.extra_damage_vulnerabilities = copyMe.extra_damage_vulnerabilities;

    this.languages = copyMe.languages;
    this.senses = { ...copyMe.senses };
    this.current_senses = { ...copyMe.current_senses };

    this.value = copyMe.value;
    this.notes = copyMe.notes;
    this._image_url = copyMe._image_url;
  }

  clone(drop_ids = false): BasicCreatureInstance {
    const new_obj = new BasicCreatureInstance();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyCreature = async (copyMe: Creature, challenge_rating = -1, update_all = true) => {
    this.creature = copyMe;
    this.challenge_rating = challenge_rating === -1 ? copyMe.challenge_rating : challenge_rating;
    this.challenge_rating_modifier = 0;
    this.creature_id = copyMe._id;
    this.alignment = copyMe.alignment;
    this.connected = true;
    this.current_ability_scores = char_util.toAbilityScores(copyMe.ability_scores, { obj: this });
    this.ability_scores = this.current_ability_scores.clone();

    // debugger;
    this.size = SIZE_MAP[copyMe.size_num_for_cr(this.challenge_rating)];
    this.resource_holder.hit_dice = copyMe.hit_dice.map(o => char_util.toHitDice(o, { obj: this }));
    if (this.resource_holder.hit_dice.length === 1) {
      this.resource_holder.hit_dice[0].size = HIT_DICE_SIZE_MAP[SIZE_TO_NUM_MAP[this.size]];
    }
    this.hit_dice_bonus = char_util.value(copyMe.hit_dice_bonus, { obj: this });
    this.hp.max = this.hit_dice.length === 0 ? this.hit_dice_bonus : Math.floor(
      this.hit_dice.map(o => {
        return o.count * ((o.size + 1) / 2) + o.count * this.hit_dice_bonus;
      }).reduce((a,b) => a + b, 0) // + this.hit_dice_bonus
    );
    if (this.hp.max === 0) {
      this.hp.max = this.hit_dice_bonus;
    }
    if (copyMe.starting_hp) {
      this.hp.current = char_util.value(copyMe.starting_hp, { obj: this });
      if (this.hp.current < 0) {
        this.hp.allow_negative = true;
      }
    } else {
      this.hp.current = this.hp.max;
    }

    const attributes = copyMe.attributes_table.getEntry(this.challenge_rating);
    if (attributes) {
      this.current_speed = {...attributes.speeds};
      this.speed = {...attributes.speeds};
      this.saving_throws = {}; // char_util.toIStringNumHash(copyMe.saving_throws, { obj: this });
      for (const ability_score of ABILITY_SCORES) {
        let modifier = this.ability_scores.getModifier(ability_score);
        if (attributes.save_profs.includes(ability_score as AbilityScoreType)) {
          modifier += this.proficiency_modifier;
        }
        this.saving_throws[ability_score] = modifier;
      }
      this.skill_proficiencies2 = [];
      const skill_ids = Object.keys(attributes.skill_profs);
      // console.log(skill_ids);
      for (const skill_id of skill_ids) {
        const skill = SKILLS.find(o => o._id === skill_id);
        if (skill) {
          let modifier = this.ability_scores.getModifier(skill.use_ability_score);
          modifier += this.proficiency_modifier * attributes.skill_profs[skill_id];
          this.skill_proficiencies2.push({ 
            skill_id, 
            prof_level: attributes.skill_profs[skill_id], 
            skill_name: skill.name,
            modifier
          });
        }
      }
      this.tool_proficiencies = [...attributes.tool_profs];
      // this.xp = copyMe.xp.value({ obj: this });
      this.favorite_conditions = [];
      // this.condition_hash = {};
      this.conditions = [];
      this.condition_immunities = [...attributes.condition_immunities];
  
      this.damage_immunities = [...attributes.damage_immunities];
      this.extra_damage_immunities = attributes.extra_damage_immunities;
      this.damage_resistances = [...attributes.damage_resistances];
      this.extra_damage_resistances = attributes.extra_damage_resistances;
      this.damage_vulnerabilities = [...attributes.damage_vulnerabilities];
      this.extra_damage_vulnerabilities = attributes.extra_damage_vulnerabilities;
  
      this.languages = copyMe.languages;
      this.senses = { ...attributes.senses };
      // debugger;
      this.current_senses = { ...attributes.senses };
      this.passives = {
        passive_perception: 10 + this.ability_scores.WIS,
        passive_insight: 10 + this.ability_scores.WIS,
        passive_investigation: 10 + this.ability_scores.INT
      };
      const perception = SKILLS.find(o => o.name === "Perception");
      const insight = SKILLS.find(o => o.name === "Insight");
      const investigation = SKILLS.find(o => o.name === "Investigation");
      if (perception && insight && investigation) {
        if (attributes.skill_profs[perception._id]) {
          this.passives.passive_perception += this.proficiency_modifier * attributes.skill_profs[perception._id];
        }
        if (attributes.skill_profs[insight._id]) {
          this.passives.passive_insight += this.proficiency_modifier * attributes.skill_profs[insight._id];
        }
        if (attributes.skill_profs[investigation._id]) {
          this.passives.passive_investigation += this.proficiency_modifier * attributes.skill_profs[investigation._id];
        }
      }
    }
    this.initiative_modifier = char_util.value(copyMe.initiative_modifier, { obj: this });
    if (copyMe.initiative_modifier.isBlank()) {
      this.initiative_modifier = this.ability_scores.DEX;
    }
    this.armor_class = char_util.value(copyMe.armor_class, { obj: this });

    this.abilities = [];

    const new_resources: {
      resource_id: string, // Can be "Slot:Level" or "Pact:Level"
      used: number,
      total: number,
      size: number
    }[] = [];
    
    const resource_entry = copyMe.resources_table.getEntry(this.challenge_rating);
    if (resource_entry) {
      for (const field of copyMe.resources_table.fields.filter(o => !o.endsWith("_size"))) {
        const resource = resource_entry.data[field];
        const size = resource_entry.data[`${field}_size`];
        new_resources.push({
          resource_id: field,
          used: 0,
          total: resource,
          size: size === undefined ? 1 : size
        });
      }
    }
    this.use_resources = new_resources;

    await this.connectCreature(copyMe);
  }

  // copyCreatureGift(creature_gift: CreatureGift, level: number) {
  //   const cig = new CreatureInstanceGift();
  //   cig.gift = creature_gift;
  //   cig.gift_id = creature_gift._id;
  //   cig.level = level;
  //   // This stuff only gets on copyCreature, not connectCreature.
  //   // This means that when it's getting changed by applying or removing a cg, 
  //   // it should only be altered at that time.
  //   const table_entry = creature_gift.table.getEntry(cig.level);
  //   if (table_entry) {
  //     for (const ability_score of ABILITY_SCORES) {
  //       this.current_ability_scores.addAbilityScore(ability_score, table_entry.data[ability_score]);
  //     }
  //     this.ability_scores = this.current_ability_scores.clone();

  //     // debugger;
  //     if (table_entry.data["Size"] !== 0) {
  //       const size_num = SIZE_TO_NUM_MAP[this.size] + table_entry.data["Size"];
  //       this.size = SIZE_MAP[size_num];
  //       if (this.resource_holder.hit_dice.length === 1) {
  //         this.resource_holder.hit_dice[0].size = HIT_DICE_SIZE_MAP[SIZE_TO_NUM_MAP[this.size]];
  //       }
  //     }
  //     if (table_entry.data["Hit Dice Count"] !== 0 && this.hit_dice.length === 1) {
  //       this.hit_dice[0].count += table_entry.data["Hit Dice Count"];
  //     }
  //     this.hit_dice_bonus = this.current_ability_scores.CON;
  //     this.hp.max = this.hit_dice.length === 0 ? this.hit_dice_bonus : Math.floor(
  //       this.hit_dice.map(o => {
  //         return o.count * ((o.size + 1) / 2) + o.count * this.hit_dice_bonus;
  //       }).reduce((a,b) => a + b, 0) // + this.hit_dice_bonus
  //     );
  //     if (this.hp.max === 0) {
  //       this.hp.max = this.hit_dice_bonus;
  //     }

  //     if (table_entry.data["AC"]) {
  //       this.armor_class += table_entry.data["AC"];
  //     }
  //     if (table_entry.data["CR"]) {
  //       this.challenge_rating_modifier += table_entry.data["CR"];
  //     }
  //   }
  //   this.passives = {
  //     passive_perception: 10 + this.ability_scores.WIS,
  //     passive_insight: 10 + this.ability_scores.WIS,
  //     passive_investigation: 10 + this.ability_scores.INT
  //   };
  //   const perception = SKILLS.find(o => o.name === "Perception");
  //   const insight = SKILLS.find(o => o.name === "Insight");
  //   const investigation = SKILLS.find(o => o.name === "Investigation");
  //   const attributes = creature_gift.attributes_table.getEntry(this.challenge_rating);
  //   if (attributes) {
  //     for (const speed of Object.keys(attributes.speeds)) {
  //       this.current_speed[speed] += attributes.speeds[speed];
  //     }
  //     this.condition_immunities = [...this.condition_immunities,...attributes.condition_immunities];
  
  //     this.damage_immunities = [...this.damage_immunities,...attributes.damage_immunities.filter(o => !this.damage_immunities.includes(o))];
  //     this.extra_damage_immunities += attributes.extra_damage_immunities;
  //     this.damage_resistances = [...this.damage_resistances,...attributes.damage_resistances.filter(o => !this.damage_resistances.includes(o))];
  //     this.extra_damage_resistances += attributes.extra_damage_resistances;
  //     this.damage_vulnerabilities = [...this.damage_vulnerabilities,...attributes.damage_vulnerabilities.filter(o => !this.damage_vulnerabilities.includes(o))];
  //     this.extra_damage_vulnerabilities += attributes.extra_damage_vulnerabilities;
  
  //     // this.languages += copyMe.languages;
  //     for (const sense of Object.keys(attributes.senses)) {
  //       this.senses[sense] += attributes.senses[sense];
  //     }
  //     // debugger;
  //     if (perception && insight && investigation) {
  //       if (attributes.skill_profs[perception._id]) {
  //         this.passives.passive_perception += this.proficiency_modifier * attributes.skill_profs[perception._id];
  //       }
  //       if (attributes.skill_profs[insight._id]) {
  //         this.passives.passive_insight += this.proficiency_modifier * attributes.skill_profs[insight._id];
  //       }
  //       if (attributes.skill_profs[investigation._id]) {
  //         this.passives.passive_investigation += this.proficiency_modifier * attributes.skill_profs[investigation._id];
  //       }
  //     }
  //   }
  //   const resource_entry = creature_gift.resources_table.getEntry(this.challenge_rating);
  //   if (resource_entry) {
  //     for (const field of creature_gift.resources_table.fields.filter(o => !o.endsWith("_size"))) {
  //       const resource = resource_entry.data[field];
  //       const size = resource_entry.data[`${field}_size`];
  //       const old_resource = this.use_resources.find(o => o.resource_id === field);
  //       if (old_resource) {
  //         old_resource.total += resource;
  //         if (size) {
  //           old_resource.size += size;
  //         }
  //       } else {
  //         this.use_resources.push({
  //           resource_id: field,
  //           used: 0,
  //           total: resource,
  //           size: size === undefined ? 1 : size
  //         });
  //       }
  //     }
  //   }
  //   const legendaries_entry = creature_gift.legendaries_table.getEntry(this.challenge_rating);
  //   if (legendaries_entry) {
  //     this.legendary_actions_per_round += legendaries_entry.actions;
  //     this.legendary_resistances += legendaries_entry.resistances;
  //   }
  //   this.creature_gifts.push(cig);
  // }

  connectCreature = async (copyMe: Creature, recopy = false) => {
    this.connected = true;
    this.creature = copyMe;

    // debugger;
    this.legendary_actions_per_round = copyMe.legendary_actions_for_cr(this.challenge_rating);
    this.legendary_resistances = copyMe.legendary_resistances_for_cr(this.challenge_rating);

    const old_actions = [...this.actions];
    const old_legendary_actions = [...this.legendary_actions];
    const old_lair_actions = [...this.lair_actions];
    const old_special_abilities = [...this.traits];
    const old_abilities = [...this.abilities];
    const new_actions: CharacterFeature[] = [];
    const new_legendary_actions: CharacterFeature[] = [];
    const new_lair_actions: CharacterFeature[] = [];
    const new_special_abilities: CharacterFeature[] = [];
    const new_abilities: CharacterAbility[] = [];
    // console.log(copyMe);
    let use_fbs = copyMe.fbs.filter(o => o.level <= this.challenge_rating);
    let upgraded_fb_ids = use_fbs.map(fb => fb.upgrades_feature_base_id);
    use_fbs = use_fbs.filter(o => !upgraded_fb_ids.includes(o.true_id));
    for (const fb of use_fbs) {
      for (const f of fb.features) {
        // if (f.name.includes("Recharge")) {
        //   debugger;
        // }
        let f_finder = fb.creature_fb_type === "Lair Actions" ? old_lair_actions : 
          fb.creature_fb_type === "Legendary Actions" ? old_legendary_actions :
          fb.creature_fb_type === "Traits" ? old_special_abilities : 
          old_actions;
        f_finder = f_finder.filter(o => o.feature_id === f.true_id);
        if (f_finder.length === 0) {
          const cf = new CharacterFeature();
          cf.copyFeature(f, null, this);
          switch (fb.creature_fb_type) {
            case "Lair Actions": new_lair_actions.push(cf); break;
            case "Legendary Actions": new_legendary_actions.push(cf); break;
            case "Traits": new_special_abilities.push(cf); break;
            default: new_actions.push(cf); break;
          }
          
          const char_ability = new CharacterAbility();
          char_ability.copyFeature(cf, this, undefined);
          new_abilities.push(char_ability);
        } else {
          const cf = f_finder[0];
          cf.copyFeature(f, null, this);
          switch (fb.creature_fb_type) {
            case "Lair Actions": new_lair_actions.push(cf); break;
            case "Legendary Actions": new_legendary_actions.push(cf); break;
            case "Traits": new_special_abilities.push(cf); break;
            default: new_actions.push(cf); break;
          }
          let char_ability = old_abilities.find(o => o.true_id === cf.true_id);
          if (!char_ability) {
            char_ability = new CharacterAbility();
          }
          char_ability.copyFeature(cf, this, undefined);
          new_abilities.push(char_ability);
        }
      }
    }
    if (recopy) {
      // const olds = this.creature_gifts.map(o => o.clone());
      // this.creature_gifts = [];
      // this.challenge_rating_modifier = 0;
      // if (olds.length > 0) {
      //   const creature_gifts = await data_util.getCreatureGifts();
      //   for (const old of olds) {
      //     const gift = creature_gifts.find(o => o._id === old.gift_id);
      //     if (gift) {
      //       this.copyCreatureGift(gift, old.level);
      //     }
      //   }
      // }
    } else {
      if (this.creature_gifts.length > 0) {
        const creature_gifts = await data_util.getCreatureGifts();
        for (const cg of this.creature_gifts) {
          const creature_gift = creature_gifts.find(o => o._id === cg.gift_id);
          if (creature_gift) {

            cg.connectCreatureGift(creature_gift);

            use_fbs = creature_gift.fbs.filter(o => o.level <= cg.level);
            upgraded_fb_ids = use_fbs.map(fb => fb.upgrades_feature_base_id);  
            use_fbs = use_fbs.filter(o => !upgraded_fb_ids.includes(o.true_id));
            for (const fb of use_fbs) {
              for (const f of fb.features) {
                let f_finder = fb.creature_fb_type === "Lair Actions" ? old_lair_actions : 
                  fb.creature_fb_type === "Legendary Actions" ? old_legendary_actions :
                  fb.creature_fb_type === "Traits" ? old_special_abilities : 
                  old_actions;
                f_finder = f_finder.filter(o => o.feature_id === f.true_id);
                if (f_finder.length === 0) {
                  const cf = new CharacterFeature();
                  cf.copyFeature(f, null, this);
                  switch (fb.creature_fb_type) {
                    case "Lair Actions": new_lair_actions.push(cf); break;
                    case "Legendary Actions": new_legendary_actions.push(cf); break;
                    case "Traits": new_special_abilities.push(cf); break;
                    default: new_actions.push(cf); break;
                  }

                  const char_ability = new CharacterAbility();
                  char_ability.copyFeature(cf, this, undefined);
                  new_abilities.push(char_ability);
                } else {
                  const cf = f_finder[0];
                  cf.copyFeature(f, null, this);
                  switch (fb.creature_fb_type) {
                    case "Lair Actions": new_lair_actions.push(cf); break;
                    case "Legendary Actions": new_legendary_actions.push(cf); break;
                    case "Traits": new_special_abilities.push(cf); break;
                    default: new_actions.push(cf); break;
                  }
                  const char_ability = old_abilities.find(o => o.true_id === cf.true_id);
                  if (char_ability) {
                    char_ability.copyFeature(cf, this, undefined);
                    new_abilities.push(char_ability);
                  }
                }
              }
            }
          }
        }
      }
    }
    this.current_senses = { ...this.senses };
    // this.use_resources = new_resources;
    this.actions = new_actions;
    this.legendary_actions = new_legendary_actions;
    this.lair_actions = new_lair_actions;
    this.traits = new_special_abilities;
    this.abilities = new_abilities;
  }

  change_challenge_rating = async (new_cr: number) => {
    if (this.creature) {
      // debugger;
      await this.copyCreature(this.creature, new_cr, false);
      // if (this.creature_gifts.length > 0) {
      //   const cigs = this.creature_gifts.map(o => { return { gift: o.gift, level: o.level }; });
      //   this.creature_gifts = [];
      //   for (const cig of cigs) {
      //     if (cig.gift) {
      //       this.copyCreatureGift(cig.gift, cig.level);
      //     }
      //   }
      //   await this.connectCreature(this.creature);
      // }
      this.subscription_holder.recalculated();
    }
  }
}

export class CreatureInstanceGift {
  true_id = "";
  gift_id = "";
  level = 1;

  gift: CreatureGift | undefined = undefined;
  
  actions: CharacterFeature[] = [];
  traits: CharacterFeature[] = [];
  legendary_actions: CharacterFeature[] = [];
  lair_actions: CharacterFeature[] = [];
  abilities: CharacterAbility[] = [];

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.gift_id = obj.gift_id ? obj.gift_id : "";
      this.level = obj.level ? obj.level : 1;
      
      this.actions = [];
      if (obj.actions && obj.actions.length > 0) {
        obj.actions.forEach((o: any) => {
          this.actions.push(new CharacterFeature(o));
        });
      }

      this.traits = [];
      if (obj.special_abilities && obj.special_abilities.length > 0) {
        obj.special_abilities.forEach((o: any) => {
          this.traits.push(new CharacterFeature(o));
        });
      }

      this.legendary_actions = [];
      if (obj.legendary_actions && obj.legendary_actions.length > 0) {
        obj.legendary_actions.forEach((o: any) => {
          this.legendary_actions.push(new CharacterFeature(o));
        });
      }
      this.lair_actions = [];
      if (obj.lair_actions && obj.lair_actions.length > 0) {
        obj.lair_actions.forEach((o: any) => {
          this.lair_actions.push(new CharacterFeature(o));
        });
      }

      this.abilities = [];
      if (obj.abilities && obj.abilities.length > 0) {
        obj.abilities.forEach((o: any) => {
          this.abilities.push(new CharacterAbility(o));
        });
      }
    }
  }

  get damage_type_overrides_table() {
    return this.gift ? this.gift.damage_type_overrides_table : new DamageTypeOverridesTable();
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      gift_id: this.gift_id,
      level: this.level,
      actions: this.actions.map(o => o.toDBObj()),
      legendary_actions: this.legendary_actions.map(o => o.toDBObj()),
      lair_actions: this.lair_actions.map(o => o.toDBObj()),
      special_abilities: this.traits.map(o => o.toDBObj()),
    };
  }

  connectCreatureGift(cg: CreatureGift) {
    this.gift_id = cg._id;
    this.gift = cg;
  }

  clone(): CreatureInstanceGift {
    const clone = new CreatureInstanceGift();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CreatureInstanceGift): void {
    this.true_id = copyMe.true_id;
    this.gift_id = copyMe.gift_id;
    this.gift = copyMe.gift;
    this.level = copyMe.level;

    this.actions = copyMe.actions.map(o => o.clone());
    this.legendary_actions = copyMe.legendary_actions.map(o => o.clone());
    this.lair_actions = copyMe.lair_actions.map(o => o.clone());
    this.traits = copyMe.traits.map(o => o.clone());
    this.abilities = copyMe.abilities.map(o => o.clone());
  }
}

/**
 * A ComplexCreatureInstance is essentially a Character. 
 * There's some things that will be done with it a little differently though, 
 * and these things will make its handling a little simpler in most ways.
 * 
 * Firstly, they generally don't have the ABCs (Ancestry, Background, Class). Instead they have a Creature. 
 * This acts as their main source cfs, but they can be given more. 
 * At first I'll make this doable through Creature Gifts and Conditions.
 * 
 * Unlike a Character, Complex Creature Instances can be Minions, 
 * or can be controlled like Basic Creature Instances in Encounters, 
 * and they will use an interface similar to the one I currently use for Basic Creature Instances, 
 * which is modeled after the formatted Creature Stat Block. 
 * That kind of display isn't really reasonable for a Character, 
 * which will generally have lots more abilities than even a Complex Creature Instance.
 */
export class ComplexCreatureInstance extends PlayerObject implements CharacterSheetInterface, CreatureDetails { // aka CCI or cci
  data_type: DataType = "complex_creature";

  creature_id: string = "";
  creature_cfs?: CCIFeatureSource;
  transform_id: string = "";
  transform?: CCIFeatureSource;
  // It looks to me like upgrade_char_features aren't actually getting used anywhere. 
  // Experiment with it for Reanimator and Carrion Hunter
  upgrade_char_features: CharacterFeature[] = []; // These hold SummonUpgradeFeatures from the char that summoned them.
  creature_gift_cfss: CCIFeatureSource[] = [];
  _cgift_ids: string[] = [];
  get cgift_ids() {
    if (this.creature_gift_cfss.length > 0) {
      return this.creature_gift_cfss.map(o => o.source_id);
    }
    return this._cgift_ids;
  }
  summon_instance?: SummonInstance;
  get summon() {
    return this.summon_instance?.summon??undefined;
  }
  summon_source_true_id = "";
  summon_cf_true_id = "";
  summon_option_true_id = "";
  summon_base_slot_level: SpellLevel = -1;
  summon_slot_level: SpellLevel = -1;
  summon_cfs?: CCIFeatureSource;
  extra_cfss: CharacterFeatureSource[] = [];
  owner_char?: Character;

  challenge_rating: number = 0;
  get character_level() { return this.challenge_rating; }
  use_your_pb = false;
  your_pb = 0;
  override_creature_type?: CreatureType | undefined;
  _creature_type: CreatureType = "None";
  
  used_legendary_actions = 0;
  legendary_actions_per_round = 0;
  used_legendary_resistances = 0;
  legendary_resistances = 0;
  _true_id = "";

  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};

  get size() {
    const size = this.base_size + this.size_modifier;

    if (isSizeType(size)) {
      return size;
    } else if (size > 6) {
      return 6;
    }
    return 0;
  }
  get base_size() {
    let size = 0;
    if (this.creature) {
      size = this.creature.size_num_for_cr(this.challenge_rating);
    } else if (this.summon) {
      size = this.summon.size_num;
    }

    if (isSizeType(size)) {
      return size;
    } else if (size > 6) {
      return 6;
    }
    return 0;
  }
  size_modifier: number = 0;
  overriding_size: SizeType = 0;
  size_option_features: SizeOptions[] = [];
  size_options: SizeType[] = [];
  get use_size() { return this.size; }
  size_bonus_for_carrying: number = 0;
  carrying_capacity_mult: number = 0;
  max_carrying_capacity: number = 0;

  current_ability_scores: AbilityScores = new AbilityScores();
  max_ability_scores: AbilityScores = new AbilityScores();
  bonus_ability_score_modifiers: AbilityScores = new AbilityScores();
  override_ability_scores: AbilityScores = new AbilityScores();
  advantage: number = 0;
  greater_advantages: GreaterAdvantage[] = [];
  inspiration: boolean = false;
  resource_holder: CharacterResourceHolder = new CharacterResourceHolder();

  resources: CharacterResource[] = [];
  slots: CharacterSlot[] = [];
  use_slots: CharacterSlot[] = [];
  infusions_by_level: CharacterInfusionsOfLevel[] = [];
  infusions: CharacterInfusion[] = [];
  spells: CharacterSpell[] = [];
  ritual_only: CharacterSpell[] = [];
  abilities: CharacterAbility[] = [];
  spell_as_abilities: CharacterAbility[] = [];
  languages_known: string[] = [];
  proficiencies: CharacterProficiency[] = [];
  jack_of_all_trades: boolean = false;
  table_modifiers: Modifier[] = [];
  spell_modifiers: CharacterAbilityModifier[] = [];
  passive_bonuses: { perception: number; insight: number; investigation: number; } = { perception: 10, insight: 10, investigation: 10 };
  range_bonuses: RangeBonus[] = [];
  rerolls: Reroll[] = [];
  minimum_rolls: MinimumRoll[] = [];
  speed: { walk: number; swim: number; climb: number; fly: number; burrow: number; } = {walk: 0, swim: 0, climb: 0, fly: 0, burrow: 0};

  attack_bonuses: Bonus[] = [];
  damage_bonuses: Bonus[] = [];
  check_bonuses: Bonus[] = [];
  share_initiative = false;
  initiative_modifier: number = 0;
  initiative_bonuses: Bonus[] = [];
  base_armor_class: number = 0;
  armor_class: number = 0;
  max_ac_dex_bonus_for_med_armor: number = 0;
  ability_checks: { 
    STR: CharacterRoll; 
    DEX: CharacterRoll; 
    CON: CharacterRoll; 
    INT: CharacterRoll; 
    WIS: CharacterRoll; 
    CHA: CharacterRoll; 
  } = { 
    STR: new CharacterRoll(), 
    DEX: new CharacterRoll(), 
    CON: new CharacterRoll(), 
    INT: new CharacterRoll(), 
    WIS: new CharacterRoll(), 
    CHA: new CharacterRoll(), 
  };
  initiative_check: CharacterRoll = new CharacterRoll();
  saving_throw_bonuses: Bonus[] = [];
  saving_throw_profs: { [ability_score: string]: number; } = {};
  saving_throws: { [ability_score: string]: CharacterRoll; } = {};
  get languages() {
    if (this.creature) {
      return this.creature.languages;
    } else if (this.summon) {
      return this.summon.languages;
    }
    return "";
  }
  AC_customization: Customization = {};
  speed_customizations: Customization = {};
  sense_customizations: Customization = {};
  display_speed: "walk" | "swim" | "climb" | "fly" | "burrow" = "walk";
  saving_throw_customizations: Customization = {};
  skill_customizations: Customization = {};
  max_ability_score_customizations: Customization = {};
  ability_score_customizations: Customization = {};
  non_skill_prof_customizations: { [prof_type: string]: { id: string; proficiency: boolean | null; notes: string; }[]; } = {};
  skill_checks: { skill_id: string; skill_check: CharacterRoll; }[] = [];
  senses: CharacterSense[] = [];
  advantages: CharacterAdvantage[] = [];
  damage_multipliers: DamageMultiplierSimple[] = [];
  extra_attacks: number = 0;
  unarmed_strike_scores: string[] = [];
  unarmed_strike_bonus_action: boolean = false;
  unarmed_strike_damage_types: DamageType[] = [];
  unarmed_strike_size: number = 0;
  unarmed_strike_count: number = 0;
  unarmed_strike_add_attack_to_dmg: boolean = false;
  show_extra_conditions: string[] = [];
  actions: CharacterAction[] = [];
  skill_proficiencies: CharacterProficiency[] = [];
  tool_proficiencies: CharacterProficiency[] = [];
  true_max_hit_points: number = 0;
  special_proficiencies: CharacterProficiency[] = [];
  weapon_proficiencies: CharacterProficiency[] = [];
  armor_proficiencies: CharacterProficiency[] = [];
  saving_throw_proficiencies: CharacterProficiency[] = [];

  // Feature Holders. Populated from CFSs
  char_feature_bases: CharacterFeatureBase[] = [];
  features: CharacterFeature[] = [];
  // get features() {
  //   const features = [...this._features];
  //   if (this.summon_instance) {
  //     features.push(...this.summon_instance.traits);
  //     features.push(...this.summon_instance.action_cfs);
  //     features.push(...this.summon_instance.legendary_actions);
  //     features.push(...this.summon_instance.lair_actions);
  //   } 
  //   return features;
  // }
  // set features(value: CharacterFeature[]) {
  //   this._features = value;
  // }
  resource_features: CharacterFeature[] = [];
  ability_features: CharacterFeature[] = [];
  characteristic_features: CharacterFeature[] = [];
  asi_features: CharacterFeature[] = [];
  ability_score_features: { cf: CharacterFeature; casibf: CharacterASIBaseFeature; }[] = [];
  language_features: CharacterFeature[] = [];
  proficiency_features: CharacterFeature[] = [];
  modifier_features: CharacterFeature[] = [];
  spell_modifier_features: CharacterFeature[] = [];
  disable_action_features: CharacterFeature[] = [];
  attack_upgrades: CharacterFeature[] = [];
  weapon_damage_upgrades: CharacterFeature[] = [];
  extra_effects_features: CharacterFeature[] = [];
  extra_effects: CharacterExtraEffects[] = [];
  light_attack_add_damage_mods: CharacterFeature[] = [];
  source_features: CharacterFeature[] = [];
  sense_features: CharacterFeature[] = [];
  start_equipment_features: CharacterFeature[] = [];
  misc_features: CharacterFeature[] = [];
  spellcasting_features: CharacterFeature[] = [];
  alternate_condition_features: AlternateConditionFeature[] = [];
  ignore_stealth_disadvantage_for_armor_type_ids: string[] = [];
  transform_upgrades: CharacterFeature[] = [];
  formula_values: { [key: string]: number; } = {};
  skill_ability_overrides: SkillAbilityOverrideFeature[] = [];
  spellcasting_ability: string = "";
  spell_dc: UpgradableNumber = new UpgradableNumber();
  spell_attack: UpgradableNumber = new UpgradableNumber();
  non_magic_attack: UpgradableNumber = new UpgradableNumber();
  magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();
  non_magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();
  string_hash: { [key: string]: string; } = {};
  upgradable_hash: { [key: string]: UpgradableNumber; } = {};
  connected = false;
  conditions: CharacterCondition[] = [];
  favorite_conditions: string[] = [];
  sf_abilities: CharacterFeature[] = [];

  damage_immunities: DamageType[] = [];
  extra_damage_immunities = "";
  damage_resistances: DamageType[] = [];
  extra_damage_resistances = "";
  damage_vulnerabilities: DamageType[] = [];
  extra_damage_vulnerabilities = "";
  condition_immunities: string[] = [];
  
  get traits() {
    return this.char_features.filter(o => o.creature_fb_type === "Traits");
  }
  get legendary_actions() {
    return this.char_features.filter(o => o.creature_fb_type === "Legendary Actions");
  }
  get lair_actions() {
    return this.char_features.filter(o => o.creature_fb_type === "Lair Actions");
  }

  changed() {
    this.subscription_holder.characterChanged();
  }
  get swarm() {
    return this.creature?.swarm??false;
  }
  get creature_type_string() {
    if (this.swarm) {
      return `Swarm of ${SIZE_MAP[this.size]} ${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    } else {
      return `${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    }
  }
  get use_ac() {
    return this.armor_class;
  }
  get size_num() {
    return this.size;
  }

  get all_char_feature_sources() {
    const all_cfs: CharacterFeatureSource[] = [];
    if (this.creature_cfs) {
      all_cfs.push(this.creature_cfs);
    }
    if (this.transform) {
      all_cfs.push(this.transform);
    }
    all_cfs.push(...this.creature_gift_cfss,...this.extra_cfss,...this.conditions);
    return all_cfs;
  }
  get items() {
    return this.extra_cfss.filter(o => o instanceof CharacterItem) as CharacterItem[];
  }
  get feats() {
    return this.extra_cfss.filter(o => o instanceof CharacterFeat) as CharacterFeat[];
  }
  get tags() {
    const tags = this.all_char_feature_sources.flatMap(o => o.tags);
    console.log(tags);
    if (this.summon) {
      tags.push(...this.summon.tags);
    } 
    return tags;
  }
  get age_category() {
    return this.creature?.age_category_for_cr(this.challenge_rating)??"";
  }

  get char_features() {
    return this.char_feature_bases.flatMap(o => o.features);
  }
  get action_cfs(): CharacterFeature[] {
    return this.char_features.filter(o => 
      (o.creature_fb_type === "Actions" || o.creature_fb_type === "") &&
      (!(o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability || 
        o.floating_feature instanceof MinionAbility) || 
      (o.floating_feature.casting_time !== "BA" && o.floating_feature.casting_time !== "RA")));
  }
  get bonus_actions() {
    return this.char_features.filter(o => 
      (o.creature_fb_type === "Actions" || o.creature_fb_type === "") &&
      ((o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability || 
        o.floating_feature instanceof MinionAbility) && 
      o.floating_feature.casting_time === "BA"));
  }
  get reactions() {
    return this.char_features.filter(o => 
      (o.creature_fb_type === "Actions" || o.creature_fb_type === "") &&
      ((o.floating_feature instanceof CreatureAbility || 
        o.floating_feature instanceof Ability || 
        o.floating_feature instanceof MinionAbility) && 
      o.floating_feature.casting_time === "RA"));
  }

  get alignment() {
    if (this.creature) {
      return this.creature.alignment;
    } else if (this.summon) {
      return this.summon.alignment;
    }
    return "Unaligned";
  }

  get creature() {
    if (this.creature_cfs && this.creature_cfs.cci_source instanceof Creature) {
      return this.creature_cfs.cci_source;
    }
    return undefined;
  }

  get true_id() { 
    return this._id === "" ? this._true_id : this._id; 
  }
  get use_challenge_rating() { return this.challenge_rating; }

  get use_resources() {
    const use_resources: {
      resource_id: string, // Can be "Slot:Level" or "Pact:Level"
      used: number,
      total: number,
      size: number
    }[] = [];
    const resources = this.resource_holder;
    for (const slot of resources.slots) {
      const resource_id = `${slot.slot_name}:${slot.level}`;
      const used = slot.used;
      const total = slot.total;
      const size = 0;
      use_resources.push({ resource_id, used, total, size });
    }
    for (const resource of resources.resources) {
      const resource_id = resource.type_id;
      const used = resource.used;
      const total = resource.total;
      const size = resource.size;
      use_resources.push({ resource_id, used, total, size });
    }
    return use_resources;
  }

  get use_spells() {
    // THIS IS HOW IT'S DONE FOR BASIC ONES. 
    // I NEED TO CHANGE IT TO WORK WITH CREATURE GIFTS AND OTHER FEATURE SOURCES AS WELL.
    if (this.creature) {
      const spellcasting_entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.spells;
      }
    }
    return [];
  }

  get spellcasting_description(): string {
    if (this.creature) {
      const spellcasting_entry = this.creature.spellcasting_table.getEntry(this.challenge_rating);
      if (spellcasting_entry) {
        return spellcasting_entry.description;
      }
    }
    return "";
  }

  get use_slots2() {
    return this.use_resources.filter(o => o.resource_id.includes("Slot"));
  }

  get hp() {
    return this.resource_holder.hp;
  }
  hit_dice: HitDice[] = [];

  get hit_dice_string() {
    let response = "";
    let hd_bonus = 0;
    for (const hd of this.hit_dice) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
      hd_bonus += hd.count * this.current_ability_scores.CON;
    }
    if (hd_bonus !== 0) {
      response += ` ${common_util.addPlusMaybe(hd_bonus)}`;
    }
    return response;
  }

  get speed_string(): string {
    let response = "";
    const speeds = Object.keys(this.speed) as SpeedType[];
    for (const key of speeds) {
      if (this.speed[key]) {
        if (response.length > 0) {
          response += ", ";
        }
        // if (key === "hover") {
        //   if (this.speed[key] === 1) {
        //     response += "hover";
        //   } else {
        //     response += "no hover";
        //   }
        // } else {
        response += `${key} ${this.speed[key]} ft.`;
        // }
      }
    }
    return response;
  }
  get flying() {
    return this.speed.fly > 0;
  }
  get swimming() {
    return this.speed.swim > 0;
  }

  get_saving_throw(ability: AbilityScoreType) {
    const roll = this.saving_throws[ability];
    if (roll) {
      return roll.modifier;
    } else {
      const modifier = this.current_ability_scores.getModifier(ability);
      if (!modifier) {
        return 0
      } else {
        return modifier;
      }
    }
  }
  saving_throw(ability: AbilityScoreType) {
    return this.get_saving_throw(ability);
  }

  get skills_string(): string {
    return this.skill_proficiencies2.map(o => { return `${o.skill_name}: ${o.modifier < 0 ? o.modifier : "+" + o.modifier}`; }).join(", ");
  }
  get skill_proficiencies2(): { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] {
    let profs: { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] = [];
    
    profs = this.skill_proficiencies.map(o => { 
      const skill = SKILLS.find(o2 => o2._id === o.proficiency_id);
      if (skill) {
        const modifier = this.proficiency_modifier * o.proficiency_level + this.current_ability_scores.getModifier(skill.use_ability_score);
        return {
          skill_id: skill._id, prof_level: o.proficiency_level, skill_name: skill.name, modifier
        }; 
      } 
      return { skill_id: "", prof_level: 0, skill_name: "", modifier: 0 };
    });
    return profs;
  }

  get tools_string() {
    return this.tool_proficiencies.join(", ");
  }

  get senses_string() {
    const response = this.senses.map(o => `${o.name}: ${o.range} ft.`).join(", ");
    return response;
  }
  get passive_perception() {
    let passive_perception = 10 + this.current_ability_scores.WIS;
    const perception = this.skill_proficiencies.find(o => o.proficiency_id === "5fef742e7494ba20cca8a9ec" || o.proficiency_id === "Perception");
    if (perception) {
      passive_perception += this.proficiency_modifier * perception.proficiency_level;
    }
    return passive_perception;
  }
  get small_name() {
    return this.creature ? this.creature.small_name : this.name;
  }

  expertises: CharacterProficiency[] = [];

  get proficiency_modifier() {
    if (this.use_your_pb && this.your_pb > 0) {
      return this.your_pb;
    }
    return common_util.proficiencyBonus(this.challenge_rating);
  }

  get creature_type() {
    if (this.override_creature_type && this.override_creature_type !== "None") {
      return this.override_creature_type;
    }
    if (this._creature_type === "None") {
      if (this.creature) {
        return this.creature.creature_type;
      } else if (this.summon) {
        return this.summon.creature_type;
      }
    }
    return this._creature_type;
  }
  set creature_type(value: CreatureType) {
    this._creature_type = value;
  }
  get subtypes() {
    if (this.creature) {
      return this.creature.subtypes;
    } else if (this.summon) {
      return this.summon.subtypes;
    }
    return [];
  }

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }

  get immunities_string(): string {
    let str = this.damage_immunities.join(", ");
    if (this.extra_damage_immunities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_immunities;
    }
    return str;
  }

  get resistances_string(): string {
    let str = this.damage_resistances.join(", ");
    if (this.extra_damage_resistances.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_resistances;
    }
    return str;
  }

  get vulnerabilities_string(): string {
    let str = this.damage_vulnerabilities.join(", ");
    if (this.extra_damage_vulnerabilities.length > 0) {
      if (str.length > 0) {
        str += ", ";
      }
      str += this.extra_damage_vulnerabilities;
    }
    return str;
  }

  get condition_immunities_string(): string {
    return this.condition_immunities.join(", ");
  }

  notes = "";
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this._true_id = obj._true_id ? obj._true_id : uuidv4().toString();
      this.creature_id = obj.creature_id ? obj.creature_id : "";

      this.challenge_rating = obj.challenge_rating ? obj.challenge_rating : 0;
      this.use_your_pb = obj.use_your_pb ? obj.use_your_pb : false;
      this.your_pb = obj.your_pb ? obj.your_pb : 0;
      this.initiative_modifier = obj.initiative_modifier ? obj.initiative_modifier : 0;
      this.share_initiative = obj.share_initiative ? obj.share_initiative : false;
      this.summon_source_true_id = obj.summon_source_true_id ? obj.summon_source_true_id : "";
      this.summon_option_true_id = obj.summon_option_true_id ? obj.summon_option_true_id : "";
      this.summon_cf_true_id = obj.summon_cf_true_id ? obj.summon_cf_true_id : "";
      this.summon_base_slot_level = obj.summon_base_slot_level ? obj.summon_base_slot_level : -1;
      this.summon_slot_level = obj.summon_slot_level ? obj.summon_slot_level : -1;

      this.owner_id = obj.owner_id ? obj.owner_id : "";
      this.image_url = obj.image_url ? obj.image_url : "";
      this.is_favorite = obj.is_favorite ? obj.is_favorite : false;
      this.is_archived = obj.is_archived ? obj.is_archived : false;
      this.inspiration = obj.inspiration ? true : false;
      this.size_modifier = obj.size_modifier ? obj.size_modifier : 0;
      this.overriding_size = obj.overriding_size ? obj.overriding_size : 0;
      this.speed_customizations = obj.speed_customizations ? obj.speed_customizations : {
        walk: { value: -1, notes: "" },
        swim: { value: -1, notes: "" },
        climb: { value: -1, notes: "" },
        fly: { value: -1, notes: "" },
        burrow: { value: -1, notes: "" }
      };
      this.non_skill_prof_customizations = obj.non_skill_prof_customizations ? obj.non_skill_prof_customizations : {};
      this.saving_throw_customizations = obj.saving_throw_customizations ? obj.saving_throw_customizations : {};
      this.skill_customizations = obj.skill_customizations ? obj.skill_customizations : {};
      this.max_ability_score_customizations = obj.max_ability_score_customizations ? obj.max_ability_score_customizations : {};
      this.ability_score_customizations = obj.ability_score_customizations ? obj.ability_score_customizations : {};
      
      this.AC_customization = obj.AC_customization ? obj.AC_customization : { AC: {} };
      this.display_speed = obj.display_speed ? obj.display_speed : "walk";
      this.resource_holder = obj.resource_holder ? new CharacterResourceHolder(obj.resource_holder) : new CharacterResourceHolder();
      this.extra_cfss = obj.extra_cfss ? obj.extra_cfss.map((o: any) => new CharacterFeatureSource(o)) : [];
      this._cgift_ids = obj._cgift_ids ? obj._cgift_ids : [];
      this.summon_instance = obj.summon_instance ? new SummonInstance(obj.summon_instance) : undefined;

      this.notes = obj.notes ? obj.notes : "";
    }
  }
  get min_challenge_rating() { return this.challenge_rating; }
  get max_challenge_rating() { return this.challenge_rating; }

  get_detail_format() {
    const str = this.creature_cfs ? `${this.creature_cfs.name}: ${this.creature_type}` : this.creature_type;
    return str.trim();
  }

  apply_piece(piece_name: string, piece: any) {
    if (!piece.message && piece) {
      // if (piece_name === "classes") {
      //   console.log(piece);
      //   debugger;
      // }
      switch(piece_name) {
        case "main": 
        break;
        case "abilities": 
          // if (this.character_options.ability_score_gen_method === "Any" || 
          //   this.character_options.ability_score_gen_method === "Not Manual/Rolled") {
          //   if (piece.ability_score_gen_method) {
          //     if (this.character_options.ability_score_gen_method === "Any" || 
          //       (this.character_options.ability_score_gen_method === "Not Manual/Rolled" && 
          //         piece.ability_score_gen_method !== "Manual")) {
          //       this.ability_score_gen_method = piece.ability_score_gen_method;
          //     }
          //   }
          // } else {
          //   this.ability_score_gen_method = this.character_options.ability_score_gen_method;
          // }
          // this.base_ability_scores = piece ? new AbilityScores(piece.base_ability_scores) : new AbilityScores();
        break;
        case "features":
          // this.extra_feats = [];
          // if (piece && piece.extra_feats) {
          //   for (let i = 0; i < piece.extra_feats.length; i++) {
          //     const feat = new CharacterFeat(piece.extra_feats[i]);
          //     if (feat.source_id === "") {
          //       return "feat";
          //     }
          //     this.extra_feats.push(feat);
          //   }
          // }
          // this.extra_damage_multipliers = [];
          // if (piece && piece.extra_damage_multipliers) {
          //   for (let i = 0; i < piece.extra_damage_multipliers.length; i++) {
          //     const dm = new DamageMultiplierSimple(piece.extra_damage_multipliers[i]);
          //     this.extra_damage_multipliers.push(dm);
          //   }
          // }
          // this.favorite_conditions = piece ? piece.favorite_conditions : [];
          // this.conditions = [];
          // if (piece && piece.conditions) {
          //   for (let i = 0; i < piece.conditions.length; i++) {
          //     const cc = new CharacterCondition(piece.conditions[i]);
          //     if (cc.source_id === "") {
          //       return "condition";
          //     }
          //     this.conditions.push(cc);
          //   }
          // } else if (piece && piece.condition_hash) {
          //   const cond_ids = Object.keys(piece.condition_hash);
          //   for (const cond_id of cond_ids) {
          //     if (typeof piece.condition_hash[cond_id] === "object") {
          //       // this.condition_hash[cond_id] = piece.condition_hash[cond_id];
          //       const cc = new CharacterCondition();
          //       cc.source_id = cond_id;
          //       cc.level = piece.condition_hash[cond_id].level;
          //       cc.condition_source_true_id = piece.condition_hash[cond_id].source_true_id;
          //       this.conditions.push(cc);
          //     } else if (typeof piece.condition_hash[cond_id] === "number") {
          //       // this.condition_hash[cond_id] = { level: piece.condition_hash[cond_id], source_true_id: "" };
          //       const cc = new CharacterCondition();
          //       cc.source_id = cond_id;
          //       cc.level = piece.condition_hash[cond_id];
          //       this.conditions.push(cc);
          //     }
          //   }
          // }
          // this.condition_immunities = piece ? piece.condition_immunities : [];
          // this.advantage = piece ? +piece.advantage : 0;
          // this.concentrating_on = null;
          // if (piece && piece.concentrating_on) {
          //   if (piece.concentrating_on.ability_type) {
          //     this.concentrating_on = new CharacterAbility(piece.concentrating_on);
          //     const source_id = this.concentrating_on.source_id;
          //     if (this.concentrating_on.source_type === "Class") {
          //       const class_finder = this.classes.filter(o => o.source_id === source_id);
          //       if (class_finder.length === 1) {
          //         this.concentrating_on.connectSource(class_finder[0]);
          //       }
          //     }
          //   } else if (piece.concentrating_on.spell_id) {
          //     this.concentrating_on = new CharacterSpell(piece.concentrating_on);
          //   }
          // }
          // this.minions = [];
          // if (piece && piece.minions && piece.minions.length > 0) {
          //   // console.log(piece.minions);
          //   for (const ci of piece.minions) {
          //     // debugger;
          //     if (ci.instance_type === undefined && ci.transform_id && ci.transform_id !== "") {
          //       this.minions.push(new TransformInstance(ci));
          //     } else if (ci.instance_type && ci.instance_type === "Summon") {
          //       this.minions.push(new SummonInstance(ci));
          //     } else {
          //       this.minions.push(new BasicCreatureInstance(ci));
          //     }
          //   }
          // }
          // this.transform = piece && piece.transform && piece.transform.tool_proficiencies === undefined ? new TransformInstance(piece.transform) : null;
          // this.use_transform = piece && piece.transform && piece.use_transform ? piece.use_transform : false;
        break;
        case "hp": 
          if (piece.resource_holder) {
            this.resource_holder = new CharacterResourceHolder(piece.resource_holder);
          }
        break;
        case "spells": 
          // console.log(piece);
          let get_special_resources = false;
          this.spells = [];
          if (piece && piece.spells && piece.spells.length > 0) {
            // console.log(piece.spells);
            for (let i = 0; i < piece.spells.length; i++) {
              const char_spell = new CharacterSpell(piece.spells[i]);
              this.spells.push(char_spell);
            }
          }
          this.abilities = [];
          if (piece && piece.abilities && piece.abilities.length > 0) {
            for (let i = 0; i < piece.abilities.length; i++) {
              const ability_piece = piece.abilities[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ability = new CharacterAbility(ability_piece);
              this.abilities.push(char_ability);
            }
          }
          this.spell_as_abilities = [];
          if (piece && piece.spell_as_abilities && piece.spell_as_abilities.length > 0) {
            for (let i = 0; i < piece.spell_as_abilities.length; i++) {
              const ability_piece = piece.spell_as_abilities[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ability = new CharacterAbility(ability_piece);
              this.spell_as_abilities.push(char_ability);
            }
          }
          this.extra_effects = [];
          if (piece && piece.extra_effects && piece.extra_effects.length > 0) {
            for (let i = 0; i < piece.extra_effects.length; i++) {
              const ability_piece = piece.extra_effects[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ee = new CharacterExtraEffects(ability_piece);
              this.extra_effects.push(char_ee);
            }
          }
        break;
        case "items": 
          // this.items = [];
          // // debugger;
          // // console.log(piece.items);
          // if (piece && piece.items && piece.items.length > 0) {
          //   const types: string[] = [];
          //   for (let i = 0; i < piece.items.length; i++) {
          //     const item = piece.items[i];
          //     if (!types.includes(item.item_type)) {
          //       types.push(item.item_type);
          //     }
          //     // if (item.item_type === "Gem") {
          //     //   console.log(item);
          //     // }
          //     let char_item: CharacterItem | null = null;
          //     switch (item.item_type) {
          //       case "Armor":
          //         char_item = new CharacterArmor(item);
          //       break;
          //       case "Weapon":
          //         char_item = new CharacterWeapon(item);
          //       break;
          //       case "Ammunition":
          //         char_item = new CharacterAmmunition(item);
          //       break;
          //       case "Bag":
          //         char_item = new CharacterContainer(item);
          //         this.containers.push(char_item as CharacterContainer);
          //       break;
          //       case "Container":
          //         char_item = new CharacterContainer(item);
          //         this.containers.push(char_item as CharacterContainer);
          //       break;
          //       case "Deck":
          //         char_item = new CharacterDeck(item);
          //       break;
          //       case "Gem":
          //         char_item = new CharacterGem(item);
          //       break;
          //       case "Book":
          //         char_item = new CharacterBook(item);
          //       break;
          //       case "Custom":
          //         if (item.true_id === "Equipment") {
          //           if (this.containers.filter(o => o.true_id === "Equipment").length === 0) {
          //             char_item = new CharacterContainer(item);
          //             this.containers.push(char_item as CharacterContainer);
          //           }
          //         } else if (item.true_id === "Storage") {
          //           if (this.containers.filter(o => o.true_id === "Storage").length === 0) {
          //             char_item = new CharacterContainer(item);
          //             this.containers.push(char_item as CharacterContainer);
          //           }
          //         } else {
          //           char_item = new CharacterCustomItem(item);
          //         }
          //       break;
          //       default:
          //         char_item = new CharacterBasicItem(item);
          //       break;
          //     }
          //     if (char_item) {
          //       if (char_item.source_id === "") {
          //         return "item";
          //       }
          //       this.items.push(char_item);
          //     }
          //   }
          //   if (this.containers.filter(o => o.name === "Equipment").length === 0) {
          //     const equipment = new CharacterContainer();
          //     equipment.name = "Equipment";
          //     equipment.true_id = "Equipment";
          //     this.containers.push(equipment);
          //     this.items.push(equipment);
          //     const storage = new CharacterContainer();
          //     storage.name = "Storage";
          //     storage.true_id = "Storage";
          //     this.containers.push(storage);
          //     this.items.push(storage);
          //   }
          // }
          // this.equipped_items = [];
          // if (piece && piece.equipped_items && piece.equipped_items.length > 0) {
          //   for (let i = 0; i < piece.equipped_items.length; i++) {
          //     // All equipped items are also in items, and should have the same ids
          //     // All that gets saved to the db is the id
          //     if (this.equipped_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
          //       const obj_finder = this.items.filter(o => o.true_id === piece.equipped_items[i]);
          //       if (obj_finder.length === 1) {
          //         this.equipped_items.push(obj_finder[0]);
          //       }
          //     }
          //   }
          // }
          // this.attuned_items = [];
          // if (piece && piece.attuned_items && piece.attuned_items.length > 0) {
          //   for (let i = 0; i < piece.attuned_items.length; i++) {
          //     // All equipped items are also in items, and should have the same ids
          //     // All that gets saved to the db is the id
          //     if (this.attuned_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
          //       const obj_finder = this.items.filter(o => o.true_id === piece.attuned_items[i]);
          //       if (obj_finder.length === 1) {
          //         this.attuned_items.push(obj_finder[0]);
          //       }
          //     }
          //   }
          // }
          // this.max_attuned_items = piece ? piece.max_attuned_items : 3;
          // // console.log(piece);
          // this.money = piece && piece.money ? new Money(piece.money) : new Money();
          // this.other_possessions = piece ? piece.other_possessions : "";
          // this.equipment_container.content_weight = -1;
          // this.storage_container.content_weight = -1;
          // const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
          // this.items = this.items.filter(o => 
          //   container_ids.includes(o.true_id) || 
          //   container_ids.includes(o.in_char_container_id));
          // this.recipes_known = piece && piece.recipes_known ? piece.recipes_known.map((o: any) => new CharacterRecipe(o)) : [];
          // this.recipe_components = piece && piece.recipe_components ? piece.recipe_components.map((o: any) => new CharacterRecipeComponent(o)) : [];
        break;
        case "race": 
          // console.log("Race disappearing sometimes. Aasimar, Uriel.");
          // console.log(piece);
          // this.race = piece && piece.race ? new CharacterRace(piece.race) : new CharacterRace();
          // if (this.race.source_id === "") {
          //   return "race";
          // }
          // this.standard_features = piece && piece.standard_features ? new CharacterStandardFeatures(piece.standard_features) : new CharacterStandardFeatures();
          // if (this.standard_features.source_id === "") {
          //   return "standard_features";
          // }
        break;
        case "gifts": 
          // debugger;
          // this.gifts = [];
          // if (piece && piece.gifts && piece.gifts.length > 0) {
          //   for (let i = 0; i < piece.gifts.length; i++) {
          //     const cg = new CharacterGift(piece.gifts[i]);
          //     if (cg.source_id === "") {
          //       return "gift";
          //     }
          //     this.gifts.push(cg);
          //   }
          // }
        break;
        case "bastion_facilities": 
          // debugger;
          // this.bastion_facilities = [];
          // if (piece && piece.bastion_facilities && piece.bastion_facilities.length > 0) {
          //   for (let i = 0; i < piece.bastion_facilities.length; i++) {
          //     const cbf = new CharacterBastionFacility(piece.bastion_facilities[i]);
          //     if (cbf.source_id === "") {
          //       return "bastion_facility";
          //     }
          //     this.bastion_facilities.push(cbf);
          //   }
          // }
        break;
        case "classes": 
          // this.classes = [];
          // if (piece && piece.classes && piece.classes.length > 0) {
          //   // console.log(piece.classes[0]);
          //   const char_classes: CharacterClass[] = [];
          //   for (let i = 0; i < piece.classes.length; i++) {
          //     const cc = new CharacterClass(piece.classes[i]);
          //     if (cc.source_id === "") {
          //       return "game_class";
          //     }
          //     char_classes.push(cc);
          //   }
          //   this.classes = char_classes.sort((a, b) => (a.position > b.position ? 1 : -1));
          // }
        break;
        // case "orgs": 
        //   this.orgs = [];
        //   if (piece && piece.orgs && piece.orgs.length > 0) {
        //     for (let i = 0; i < piece.orgs.length; i++) {
        //       this.orgs.push(new CharacterOrg(piece.orgs[i]));
        //     }
        //   }
        // break;
        case "background": 
          // this.background = piece && piece.background ? new CharacterBackground(piece.background) : new CharacterBackground();
          // if (this.background.source_id === "") {
          //   return "background";
          // }
        break;
      }
      // if (piece_name === "race") {
      //   console.log(this);
      // }
    }
    return "";
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      _true_id: this._true_id,
      instance_type: "ComplexCreatureInstance",
      creature_id: this.creature_id,
      extra_cfss: this.extra_cfss.map(o => o.toDBObj()),
      image_url: this.image_url,
      base_size: this.base_size,
      size_modifier: this.size_modifier,
      overriding_size: this.overriding_size,
      speed_customizations: this.speed_customizations,
      sense_customizations: this.sense_customizations,
      saving_throw_customizations: this.saving_throw_customizations,
      skill_customizations: this.skill_customizations,
      max_ability_score_customizations: this.max_ability_score_customizations,
      ability_score_customizations: this.ability_score_customizations,
      display_speed: this.display_speed,
      AC_customization: this.AC_customization,
      non_skill_prof_customizations: this.non_skill_prof_customizations,
      resource_holder: this.resource_holder.toDBObj(),
      challenge_rating: this.challenge_rating,
      initiative_modifier: this.initiative_modifier,
      share_initiative: this.share_initiative,
      summon_source_true_id: this.summon_source_true_id,
      summon_cf_true_id: this.summon_cf_true_id,
      summon_base_slot_level: this.summon_base_slot_level,
      summon_slot_level: this.summon_slot_level,
      summon_option_true_id: this.summon_option_true_id,
      _cgift_ids: this.cgift_ids,
      summon_instance: this.summon_instance?.toDBObj()??undefined,
      notes: this.notes,
    };
  }

  toCharacterDBObj = (piece: string) => {
    let obj: any = {};
    switch (piece) {
      case "abilities": 
        obj = {
          char_id: this._id,
          // ability_score_gen_method: this.ability_score_gen_method,
          // base_ability_scores: this.base_ability_scores.toDBObj(),
        };
      break;
      case "features": 
        // const concentrating_on = this.concentrating_on ? this.concentrating_on.toDBObj() : null;
        // if (concentrating_on) {
        //   concentrating_on._name = this.concentrating_on?.name??"";
        // }
        obj = {
          char_id: this._id,
          // extra_feats: this.extra_feats.map(o => o.toDBObj()),
          // extra_damage_multipliers: this.extra_damage_multipliers.map(o => o.toDBObj()),
          // favorite_conditions: this.favorite_conditions,
          // conditions: this.conditions.map(o => o.toDBObj()),
          // condition_immunities: this.condition_immunities,
          advantage: this.advantage,
          // concentrating_on,
          // minions: this.minions.map(o => o.toDBObj()),
          // transform: this.transform ? this.transform.toDBObj() : null,
          // use_transform: this.use_transform,
          // adaptations: this.adaptations.map(o => o.toDBObj()),
        };
      break;
      case "hp": // It being called "hp" is because I used to have it only have hp.  It should be called "resources" now, but I'm going to leave it.
        obj = {
          char_id: this._id,
          resource_holder: this.resource_holder.toDBObj(),
        };
      break;
      case "spells":
        obj = {
          char_id: this._id,
          spells: this.spells.map(o => o.toDBObj()),
          abilities: this.abilities.map(o => o.toDBObj()),
          spell_as_abilities: this.spell_as_abilities.map(o => o.toDBObj()),
          extra_effects: this.extra_effects.map(o => o.toDBObj())
        };
      break;
      case "items": 
        obj = {
          char_id: this._id,
          // items: this.items.map(o => o.toDBObj()),
          // equipped_items: this.equipped_items.map(o => o.true_id),
          // attuned_items: this.attuned_items.map(o => o.true_id),
          // max_attuned_items: this.max_attuned_items,
          // other_possessions: this.other_possessions,
          // money: this.money.toDBObj(),
          // recipes_known: this.recipes_known.map(o => o.toDBObj()),
          // recipe_components: this.recipe_components.map(o => o.toDBObj()),
        };
      break;
      case "race": 
        obj = {
          char_id: this._id,
          // race: this.race.toDBObj(),
          // standard_features: this.standard_features.toDBObj(),
        };
      break;
      case "gifts": 
        obj = {
          char_id: this._id,
          // gifts: this.gifts.map(o => o.toDBObj())
        };
      break;
      case "bastion_facilities": 
        obj = {
          char_id: this._id,
          // bastion_facilities: this.bastion_facilities.map(o => o.toDBObj())
        };
      break;
      case "classes": 
        obj = {
          char_id: this._id,
          // classes: this.classes.map(o => o.toDBObj())
        };
      break;
      case "background": 
        obj = {
          char_id: this._id,
          // background: this.background.toDBObj()
        };
      break;
      case "orgs": 
        // const orgs: any[] = [];
        // for (let i = 0; i < this.orgs.length; i++) {
        //   orgs.push(this.orgs[i].toDBObj());
        // }
        // obj = {
        //   char_id: this._id,
        //   orgs
        // };
      break;
    }
    return obj;
  }

  clone(drop_ids = false): ComplexCreatureInstance {
    const clone = new ComplexCreatureInstance();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: ComplexCreatureInstance, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this._true_id = copyMe._true_id;
    this.creature_id = copyMe.creature_id;
    this.creature_cfs = copyMe.creature_cfs ? copyMe.creature_cfs.clone(drop_ids) : undefined;
    this.transform = copyMe.transform ? copyMe.transform.clone(drop_ids) : undefined;
    this.creature_gift_cfss = copyMe.creature_gift_cfss.map(o => o.clone(drop_ids));
    this._cgift_ids = copyMe.cgift_ids;
    this.owner_char = copyMe.owner_char;
    
    this.summon_instance = copyMe.summon_instance;
    this.summon_source_true_id = copyMe.summon_source_true_id;
    this.summon_cf_true_id = copyMe.summon_cf_true_id;
    this.summon_option_true_id = copyMe.summon_option_true_id;
    this.summon_base_slot_level = copyMe.summon_base_slot_level;
    this.summon_slot_level = copyMe.summon_slot_level;
    this.summon_cfs = copyMe.summon_cfs ? copyMe.summon_cfs.clone() : undefined;

    this.extra_cfss = copyMe.extra_cfss.map(o => o.clone(drop_ids));
    this.resource_holder = copyMe.resource_holder.clone();

    this.challenge_rating = copyMe.challenge_rating;
    this.use_your_pb = copyMe.use_your_pb;
    this.your_pb = copyMe.your_pb;
    this.initiative_modifier = copyMe.initiative_modifier;
    this.share_initiative = copyMe.share_initiative;
    this.notes = copyMe.notes;
    this.image_url = copyMe.image_url;
  }

  copyCI(copyMe: BasicCreatureInstance) {
    this.name = copyMe.name;
    this.creature_id = copyMe.creature_id;
    this.challenge_rating = copyMe.challenge_rating;
    if (copyMe.creature) {
      this.creature_cfs = new CCIFeatureSource();
      this.creature_cfs.copyCCISource({ feature_source: copyMe.creature, char: this });
      this.hp.max = copyMe.hp.max;
      this.hp.current = copyMe.hp.current;
    }
    this.notes = copyMe.notes;
    this.image_url = copyMe.image_url;
  }

  copyTI(copyMe: TransformInstance) {
    this.name = copyMe.name;
    this.creature_id = copyMe.creature_id;
    this.challenge_rating = copyMe.challenge_rating;
    if (copyMe.creature) {
      this.creature_cfs = new CCIFeatureSource();
      this.creature_cfs.copyCCISource({ feature_source: copyMe.creature, char: this });
    }
    this.transform_id = copyMe.transform_id;
    if (copyMe.transform) {
      this.transform = new CCIFeatureSource();
      this.transform.copyCCISource({ feature_source: copyMe.transform, char: this });
    }
    this.hp.max = copyMe.hp.max;
    this.hp.current = copyMe.hp.current;
    this.notes = copyMe.notes;
    this.image_url = copyMe.image_url;
  }

  copySI(copyMe: SummonInstance) {
    // debugger; 
    // This one isn't implemented yet. 
    // It needs to store details about the summon, which come from an ability effect, possibly from a spell or from an ability, 
    // and it will need details about the CFS used for it, and possibly the spell slot used to cast it.
    this.name = copyMe.name;
    this.creature_id = copyMe.creature_id;
    this.challenge_rating = copyMe.challenge_rating;
    this.initiative_modifier = copyMe.initiative_modifier;
    this.share_initiative = copyMe.share_initiative;
    this.use_your_pb = copyMe.use_your_pb;
    this.your_pb = copyMe.your_pb;
    if (copyMe.creature) {
      this.creature_cfs = new CCIFeatureSource();
      this.creature_cfs.copyCCISource({ feature_source: copyMe.creature, char: this });
    }
    this.summon_instance = copyMe;
    this.summon_source_true_id = copyMe.source_true_id;
    this.summon_cf_true_id = copyMe.cf_true_id;
    this.summon_base_slot_level = copyMe.base_slot_level;
    this.summon_slot_level = copyMe.slot_level;
    this.summon_option_true_id = copyMe.stat_block_id;
    this.hp.max = copyMe.hp.max;
    this.hp.current = copyMe.hp.current;
    // this.stat_block_id = copyMe.stat_block_id;
    this.notes = copyMe.notes;
    this.image_url = copyMe.image_url;
  }
}
export class CCIFeatureSource extends CharacterFeatureSource {
  cci_source?: Creature | CreatureGift | Transform;
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
    }
  }

  get name(): string {
    return this.get_use_name();
  }

  get data_type() {
    return this.source?.data_type??"";
  }

  get variant_of_id(): string {
    return this.source?.variant_of_id??"";
  }

  get variant_ids(): string[] {
    return this.source?.variant_ids??[];
  }

  get variant_group_id(): string {
    if (this.source) {
      return this.source.variant_group_id;
    }
    return "";
  }

  get_use_name(): string {
    let use_name = "";
    if (this.replace_name === "") {
      if (this.source) {
        use_name = this.source.use_name;
      }
    } else {
      return this.replace_name;
    }
    const name_bonuses = this.name_bonuses.join(" ");
    return name_bonuses.length > 0 ? name_bonuses + " " + use_name : use_name;
  }

  get tags() {
    const tags: string[] = [];
    if (this.source instanceof FeatureSource) {
      tags.push(...this.source.tags);
    }
    return [];
  }

  get description(): string {
    if (this.source) {
      return this.source.description;
    }
    return "";
  }

  get all_char_feature_bases(): CharacterFeatureBase[] {
    let all_char_feature_bases = this.char_feature_bases;
    this.sub_char_feature_sources.forEach(scfs => {
      all_char_feature_bases = [...all_char_feature_bases, ...scfs.all_char_feature_bases];
    });
    return all_char_feature_bases;
  }

  get search_ids(): string[] {
    if (this.source) {
      return this.source.search_ids;
    }
    return [];
  }

  toDBObj(): any {
    return {
      ...super.toDBObj()
    };
  }

  get feature_source_type() {
    return this.cci_source?.data_type??"";
  }

  get source_books() {
    return this.cci_source?.source_books_old??[];
  }

  clone(drop_ids = false): CCIFeatureSource {
    const clone = new CCIFeatureSource();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CCIFeatureSource, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
  }

  copyCCISource({
    feature_source, 
    char,
    log_it = false
  }: { 
    feature_source: Creature | CreatureGift | Transform, 
    char: ComplexCreatureInstance,
    log_it?: boolean 
  }) {
    // debugger;
    this.source_type = feature_source.data_type;
    this.source_name = feature_source.name;
    this.source_id = feature_source._id;
    this.cci_source = feature_source;
    this.ancestries = [{
      true_id: this.true_id,
      source_id: this.source_id,
      type: this.source_type,
      name: this.source_name,
      parent_id: ""
    }];
    this.char_feature_bases = [];
    if (feature_source instanceof Creature || 
      feature_source instanceof CreatureGift) {
      for (const fb of feature_source.fbs) {
        let char_feature_base = this.char_feature_bases.find(o => 
          o.true_id === fb.true_id);
        if (log_it) {
          console.log(fb);
          console.log(char_feature_base);
          console.log(this.char_feature_bases);
        }
        if (char_feature_base) {
          if (log_it) {
            console.log(char_feature_base);
          }
          char_feature_base.copyFeatureBase(fb, 
            this, char, log_it);
          char_feature_base.source_id = this.source_id;
          if (log_it) {
            console.log(fb);
            console.log(char_feature_base);
          }
        } else {
          char_feature_base = new CharacterFeatureBase();
          char_feature_base.copyFeatureBase(fb, 
            this, char); //, log_it);
          char_feature_base.source_id = this.source_id;
          if (log_it) {
            console.log(fb);
            console.log(char_feature_base);
          }
          this.char_feature_bases.push(char_feature_base);
        }
      }
    }
  }
}